import React, { useState, useEffect, useRef } from 'react';
import styled from '@emotion/styled';
import SliderArrowLeftIcon from '../icons/SliderArrowLeftIcon';
import SliderArrowRightIcon from '../icons/SliderArrowRightIcon';
import SliderPlayIcon from '../icons/SliderPlayIcon';
import DiscoverModePrivateIcon from '../icons/DIscoverModePrivateIcon';
import { WYP_PRIVATE_IMG_FALLBACK } from '../../config/Master';
import PrivateMonkeyIcon from '../icons/PrivateMonkeyIcon';
import { ProfileMaker } from '../../models/Profile';
import { connect, useDispatch } from 'react-redux';
import { EVENT_NAMES } from '../../constants/mixpanel';
import { Swiper, SwiperSlide } from 'swiper/react';
import 'swiper/swiper-bundle.min.css';
import 'swiper/swiper.min.css';
import { useProfile } from '../../contexts/ProfileContext';
import { updateMixpanelProfileClosedPayload } from '../../utils/mixpanel/profileClosed';
import useMixPanel from '../../hooks/useMixPanel';
import { IProfileMediaSliderProps } from './types/ProfileMediaSlider';
import { mixpanelTrackEvent } from '../../utils/mixpanel/common';
import { setCloseTooltip } from '../../actions/profileActions';

const SliderContainer = styled.div`
  position: absolute;
  width: 100%;
  height: 100%;
  overflow: hidden;
  top: 0;
`;

const Slide = styled.div`
  position: relative;

  height: 100%;
`;

const Img = styled('div')<{ imgUrl: string }>(
  {
    width: '100%',
    height: '100%',
    position: 'absolute',
    top: '0px',
    left: '0px',
    zIndex: 1,
  },
  ({ imgUrl }) => ({
    backgroundImage: `url(${imgUrl})`,
    backgroundPosition: 'top center',
    backgroundRepeat: 'no-repeat',
    backgroundSize: 'cover',
  })
);

const PrivateDetailsContainer = styled('div')({
  position: 'absolute',
  top: '50%',
  left: '50%',
  transform: 'translate(-50%,-50%)',
  textAlign: 'center',
  zIndex: 1,
});

const Gradient = styled('div')({
  background: 'linear-gradient(180deg, rgba(0, 0, 0, 0.00) 0%, #000 82.5%)',
  position: 'absolute',
  width: '100%',
  height: 130,
  bottom: 0,
  left: 0,
  zIndex: 1,
});

const MediaStatus = styled('div')({
  position: 'absolute',
  bottom: 11,
  right: 15,
  zIndex: 2,
  color: '#E2C92D',
  fontSize: 12,
  border: '1px solid #E2C92D',
  borderRadius: 4,
  lineHeight: '14px',
  letterSpacing: '-0.24px',
  padding: '6px 14px',
});

const SliderArrowContainer = styled('div')({
  position: 'absolute',
  transform: 'translate(0px,-50%)',
  top: '50%',
  height: 245,
  width: 50,
  display: 'flex',
  alignItems: 'center',
  cursor: 'pointer',
});

const AnimatedOpacityIcon = styled('div')(
  {
    opacity: 0,
    transition: 'opacity 0.3s ease',
  },
  (props: { visible: boolean }) => ({
    opacity: props.visible ? 1 : 0,
  })
);

const PrivateLabelCount = styled('p')({
  fontSize: 20,
  fontWeight: 900,
  color: '#fff',
  lineHeight: '20px',
  letterSpacing: '-0.4px',
  margin: 0,
  padding: 0,
});

const PrivateLabelInfo = styled('p')({
  fontSize: 14,
  fontWeight: 500,
  color: '#fff',
  lineHeight: '20px',
  letterSpacing: '-0.28px',
  margin: 0,
  padding: 0,
  maxWidth: 105,
});

const ProfileMediaSlider = ({
  photos,
  currentSlide,
  privateCount,
  onClickPhoto,
  profile,
  setCurrentSlide,
  isOtherProfile,
  profileDetailsComponent,
}: IProfileMediaSliderProps): JSX.Element => {
  const { setMixpanelDataByKeyHandler } = useMixPanel();
  const [arrowVisible, setArrowVisible] = useState(true);
  const [firstRenderPrevent, setFirstRenderPrevent] = useState(false);
  const { gallerySwiper, setGallerySwiper, profileDetailsRef, setAnimateDirection, setIsDotsAnimated } = useProfile();
  const hasNavigatedSlideGalleries = sessionStorage.getItem('hasNavigatedSlideGalleries');
  const [photoViewCount, setPhotoViewCount] = useState(0);
  const dispatch = useDispatch();
  const touchStartX = useRef(0);
  const touchEndX = useRef(0);
  const touchStartTranslate = useRef(0);
  const filteredPhotos = photos.filter(photo => !(photo.type === 'video' && photo.status === 'pending'));

  useEffect(() => {
    return () => {
      if (firstRenderPrevent) setFirstRenderPrevent(false);
    };
  }, [firstRenderPrevent]);

  useEffect(() => {
    setArrowVisible(true);

    const timer = setTimeout(() => {
      // add condition to check for current session
      if (currentSlide > 0 || hasNavigatedSlideGalleries) {
        setArrowVisible(false);
      }
    }, 800);

    return () => {
      clearTimeout(timer);
    };
  }, [currentSlide, photos, hasNavigatedSlideGalleries]);

  useEffect(() => {
    if (gallerySwiper?.realIndex === 0 && photos.length > 1 && photoViewCount === 0) {
      gallerySwiper.slideToLoop(0, 0);
      setFirstRenderPrevent(true);
    }
  }, [gallerySwiper, photos, photoViewCount]);

  useEffect(() => {
    if (!hasNavigatedSlideGalleries && currentSlide > 0) {
      sessionStorage.setItem('hasNavigatedSlideGalleries', 'true');
    }
  }, [currentSlide, hasNavigatedSlideGalleries]);

  useEffect(() => {
    if (photoViewCount > 0) {
      updateMixpanelProfileClosedPayload(setMixpanelDataByKeyHandler, photoViewCount);
    }
  }, [photoViewCount]);

  const isAllowedButtonClick = (element: HTMLElement | null): boolean => {
    if (!element) return false;

    const excludedElementIds = ['fav-btn', 'edit-profile-cover', 'badge-tooltip'];

    if (excludedElementIds.includes(element.id)) return true;
    return isAllowedButtonClick(element.parentElement);
  };

  useEffect(() => {
    const element = profileDetailsRef.current;

    const handleTouchStart = (e: TouchEvent) => {
      if (!isAllowedButtonClick(e.target as HTMLElement)) {
        e.preventDefault();
        dispatch(setCloseTooltip(true));
      }

      touchStartX.current = e.touches[0].clientX;
      if (gallerySwiper) {
        touchStartTranslate.current = gallerySwiper.getTranslate();
        gallerySwiper.setTransition(0); // disable transition for real-time dragging effect
      }
    };

    const handleTouchMove = (e: TouchEvent) => {
      if (!isAllowedButtonClick(e.target as HTMLElement)) {
        e.preventDefault();
      }

      if (gallerySwiper) {
        const touchEndX = e.touches[0].clientX;
        const swipeDiff = touchStartX.current - touchEndX;

        // update translate value based on the swipe difference
        gallerySwiper.setTranslate(touchStartTranslate.current - swipeDiff);
      }
    };

    const handleTouchEnd = (e: TouchEvent) => {
      if (!isAllowedButtonClick(e.target as HTMLElement)) {
        e.preventDefault();
      }

      if (gallerySwiper) {
        gallerySwiper.setTransition(300);
        gallerySwiper.updateProgress();
        gallerySwiper.updateActiveIndex();
        gallerySwiper.updateSlidesClasses();

        if (gallerySwiper.realIndex === 0) {
          gallerySwiper.slideToLoop(0, 0);
          return;
        }

        if (gallerySwiper.realIndex === photos.length - 1) {
          gallerySwiper.slideToLoop(gallerySwiper.realIndex, 0);
          return;
        }

        const touchEndX = touchStartX.current;
        const swipeDiff = touchStartX.current - touchEndX;

        if (swipeDiff > 0) {
          // swipe left
          gallerySwiper.slideToLoop(gallerySwiper.activeIndex + 1);
        } else {
          // swipe right
          gallerySwiper.slideToLoop(gallerySwiper.activeIndex - 1);
        }
      }
    };

    if (element) {
      element.addEventListener('touchstart', handleTouchStart, { passive: false });
      element.addEventListener('touchmove', handleTouchMove, { passive: false });
      element.addEventListener('touchend', handleTouchEnd, { passive: false });
    }

    return () => {
      if (element) {
        element.removeEventListener('touchstart', handleTouchStart);
        element.removeEventListener('touchmove', handleTouchMove);
        element.removeEventListener('touchend', handleTouchEnd);
      }
    };
  }, [photos, gallerySwiper, touchEndX, touchStartX, profileDetailsRef]);

  return (
    <SliderContainer>
      <Swiper
        allowSlidePrev={true}
        allowSlideNext={true}
        onSwiper={swiper => {
          setGallerySwiper(swiper);
        }}
        style={{ height: '100%' }}
        loop={photos.length > 1}
        onSlideChange={swiperCore => {
          if (firstRenderPrevent) {
            if (
              (photos.length > 2 && currentSlide == 0 && swiperCore.realIndex == photos.length - 1) ||
              (photos.length > 2 && currentSlide == photos.length - 1 && swiperCore.realIndex == 0)
            ) {
              setIsDotsAnimated(false);
            } else {
              setIsDotsAnimated(!(photos.length === 2 && (swiperCore.activeIndex > 2 || swiperCore.activeIndex === 0)));
              //right and last dot and left and first dot 0 to 1 left side
              setAnimateDirection(swiperCore.realIndex > currentSlide ? 'right' : 'left');
            }
          }

          setCurrentSlide(swiperCore.realIndex);
          setPhotoViewCount(count => count + 1);
        }}
      >
        {filteredPhotos.map((photo, index) => {
          const photoUrl = photo.image;
          const imgUrl = photo.private ? WYP_PRIVATE_IMG_FALLBACK : photoUrl;

          return (
            <SwiperSlide key={index}>
              <Slide
                onClick={e => {
                  onClickPhoto(index, e);

                  if (profile.hashId() && photo.type == 'photo') {
                    if (isOtherProfile) {
                      mixpanelTrackEvent({
                        eventName: EVENT_NAMES.LIGHTBOX_OPENED,
                        payload: {
                          eventProperties: {
                            'Lightbox Source': 'Main Photo',
                          },
                        },
                      });
                    }
                  }
                }}
              >
                <Img className="data-avatar-image-url" imgUrl={imgUrl} />
                {!photo.private && photo.type === 'video' && (
                  <SliderPlayIcon
                    style={{
                      position: 'absolute',
                      left: '50%',
                      top: '50%',
                      transform: 'translate(-50%, -35%)',
                      zIndex: 2,
                    }}
                    onClick={(e: React.MouseEvent) => {
                      onClickPhoto(index, e);
                    }}
                  />
                )}

                {photo.private && (
                  <PrivateDetailsContainer>
                    <DiscoverModePrivateIcon style={{ marginBottom: 20 }} />
                    <PrivateLabelCount>{`+ ${privateCount}`}</PrivateLabelCount>
                    <PrivateLabelInfo>Private Photos</PrivateLabelInfo>
                    <PrivateLabelInfo style={{ marginTop: 20 }}>Accepted offers get access!</PrivateLabelInfo>
                  </PrivateDetailsContainer>
                )}
                {photo.privateReal && (
                  <PrivateMonkeyIcon style={{ position: 'absolute', top: 15, right: 15, zIndex: 2 }} />
                )}

                {photo.status === 'new' && <MediaStatus>Pending</MediaStatus>}

                <Gradient />
              </Slide>
            </SwiperSlide>
          );
        })}
      </Swiper>
      {profileDetailsComponent}
      {photos.length > 1 && (
        <>
          <SliderArrowContainer
            onClick={() => {
              if (gallerySwiper) {
                gallerySwiper.slidePrev();
              }
            }}
            style={{ left: 0, paddingLeft: '15px', zIndex: 2 }}
          >
            <AnimatedOpacityIcon visible={arrowVisible}>
              <SliderArrowLeftIcon />
            </AnimatedOpacityIcon>
          </SliderArrowContainer>

          <SliderArrowContainer
            onClick={() => {
              if (gallerySwiper) {
                gallerySwiper.slideNext();
              }
            }}
            style={{ right: 0, paddingRight: '15px', zIndex: 2, justifyContent: 'flex-end' }}
          >
            <AnimatedOpacityIcon visible={arrowVisible}>
              <SliderArrowRightIcon />
            </AnimatedOpacityIcon>
          </SliderArrowContainer>
        </>
      )}
    </SliderContainer>
  );
};

const mapStateToProps = state => {
  return {
    profile: ProfileMaker.create(state.profile.profile),
  };
};

export default connect(mapStateToProps, {})(ProfileMediaSlider);
